/**
 * Slice C4 — Helpers for the `IndexAddTypeField` Type dropdown. Pulled out of
 * the TSX so the component file exports React components only (avoids
 * `react-refresh/only-export-components` warnings).
 *
 * - `IndexTypeSelectValue` is the discriminator the dropdown reads/writes.
 *   Values map 1:1 to `IndexType.kind` except for mssql columnstore, which
 *   splits into `'columnstore'` (NONCLUSTERED) and `'columnstore-clustered'`
 *   (CLUSTERED) so users can pick either via a single dropdown.
 * - `getIndexTypeOptionsForEngine(engine)` returns the option list the
 *   dropdown should render for the engine. Default ('') is always present.
 *   The `'btree'` explicit option is suppressed on every engine except mysql
 *   (only mysql treats explicit `btree` differently from omitted).
 * - `buildIndexType(value, …)` maps the dropdown value + sub-field state back
 *   to the `IndexType` payload the synthesizer consumes; returns `undefined`
 *   for the default option.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice C4.
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { IndexType } from '../../../../sql-resolver/types/ddlEdit';
import { getIndexTypeKindsForEngine } from '../../edit/edits/addIndex';

export type IndexTypeSelectValue =
  | ''
  | 'btree'
  | 'hash'
  | 'gin'
  | 'gist'
  | 'brin'
  | 'spgist'
  | 'columnstore'
  | 'columnstore-clustered'
  | 'xml'
  | 'spatial'
  | 'fulltext'
  | 'bitmap'
  | 'domain';

export interface IndexTypeDropdownOption {
  readonly value: IndexTypeSelectValue;
  readonly label: string;
}

const ALL_OPTIONS: readonly IndexTypeDropdownOption[] = [
  { value: '', label: 'Default (b-tree)' },
  { value: 'btree', label: 'BTREE (explicit)' },
  { value: 'hash', label: 'HASH' },
  { value: 'gin', label: 'GIN' },
  { value: 'gist', label: 'GiST' },
  { value: 'brin', label: 'BRIN' },
  { value: 'spgist', label: 'SP-GiST' },
  { value: 'columnstore', label: 'NONCLUSTERED COLUMNSTORE' },
  { value: 'columnstore-clustered', label: 'CLUSTERED COLUMNSTORE' },
  { value: 'xml', label: 'XML' },
  { value: 'spatial', label: 'SPATIAL' },
  { value: 'fulltext', label: 'FULLTEXT' },
  { value: 'bitmap', label: 'BITMAP' },
  { value: 'domain', label: 'DOMAIN' },
];

export function getIndexTypeOptionsForEngine(
  engine: SqlEngine,
): readonly IndexTypeDropdownOption[] {
  const supportedKinds = getIndexTypeKindsForEngine(engine);
  return ALL_OPTIONS.filter((opt) => {
    if (opt.value === '') return true;
    if (opt.value === 'btree') return engine === 'mysql';
    if (opt.value === 'columnstore-clustered') {
      return engine === 'mssql' && supportedKinds.includes('columnstore');
    }
    return supportedKinds.includes(opt.value as IndexType['kind']);
  });
}

export function buildIndexType(
  value: IndexTypeSelectValue,
  mssqlKeyIndex: string,
  mssqlCatalog: string,
  oracleIndextype: string,
): IndexType | undefined {
  if (value === '') return undefined;
  if (value === 'columnstore') return { kind: 'columnstore' };
  if (value === 'columnstore-clustered') return { kind: 'columnstore', clustered: true };
  if (value === 'fulltext') {
    const trimmedKey = mssqlKeyIndex.trim();
    const trimmedCatalog = mssqlCatalog.trim();
    return {
      kind: 'fulltext',
      ...(trimmedKey.length > 0 ? { mssqlKeyIndex: trimmedKey } : {}),
      ...(trimmedCatalog.length > 0 ? { mssqlCatalog: trimmedCatalog } : {}),
    };
  }
  if (value === 'domain') {
    return { kind: 'domain', indextype: oracleIndextype.trim() };
  }
  return { kind: value };
}
